<script setup lang="ts">
import { RouterView, useRoute, useRouter } from 'vue-router'
import { onMounted, onUnmounted, computed, watch, ref, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { Eye, X } from 'lucide-vue-next'
import { useLocaleStore } from './stores/locale'
import { useUiStore } from './stores/ui'
import { usePageStore } from './stores/pages'
import { useSectionStore } from './stores/sections'
import { useThemeStore } from './stores/theme'
import { useAuthStore } from './stores/auth'
import { useAccessStore } from './stores/access'
import { useFigmaStore } from './stores/figma'
import { usePlatformAlertStore } from './stores/platformAlerts'
import { useAppVersionStore } from './stores/appVersion'
import AccessGate from './components/access/AccessGate.vue'
import { useFocusManagement } from './composables/useFocusManagement'
import { useAnnounce } from './composables/useAnnounce'
import TheSidebar from './components/layout/TheSidebar.vue'
import TheHeader from './components/layout/TheHeader.vue'
import AppFooter from './components/layout/AppFooter.vue'
import TheAdminBar from './components/layout/TheAdminBar.vue'
import PlatformAlertBar from './components/layout/PlatformAlertBar.vue'
import NewVersionBar from './components/layout/NewVersionBar.vue'
import SidebarNav from './components/layout/SidebarNav.vue'
import LiveRegion from './components/accessibility/LiveRegion.vue'
import SkipLink from './components/accessibility/SkipLink.vue'
import NotificationToast from './components/common/NotificationToast.vue'
import SyncProgressPopover from './components/figma/SyncProgressPopover.vue'

const ui = useUiStore()
const route = useRoute()
const router = useRouter()

/**
 * Terug naar beheer. De voorbeeldweergave sprong naar het publieke adres
 * van de pagina; zonder deze stap blijf je daarop staan terwijl je weer
 * aan het beheren bent - met een menu dat inmiddels naar beheeradressen
 * wijst. Kwam je nergens vandaan, dan blijven we gewoon staan.
 */
function stopPreview() {
  const terug = ui.endPreview()
  if (terug && terug !== route.fullPath) router.push(terug)
}

const pageStore = usePageStore()
const sectionStore = useSectionStore()
const localeStore = useLocaleStore()
const { t } = useI18n()

/* ── Navigatieboom: geladen door de app-shell (niet door TheSidebar),
   want de header toont de hoofdcategorieën ook wanneer de sidebar
   verborgen is (login, homepage met verborgen menu). ── */
onMounted(() => sectionStore.fetchTree(ui.visitorView))
watch(() => ui.visitorView, (asVisitor) => sectionStore.fetchTree(asVisitor))

/* ── Thema (logo): publiek, ook voor bezoekers ── */
const themeStore = useThemeStore()
onMounted(() => themeStore.fetchTheme())

/* ── Afscherming: staat de site op slot voor deze bezoeker? ──
   Wordt vóór de inhoud getoond; de API bewaakt de data zelf, dit scherm is
   de nette voorkant daarvan. Na ontgrendelen alsnog de navigatie ophalen —
   die kwam bij het opstarten met een 403 terug. */
const accessStore = useAccessStore()
onMounted(() => accessStore.fetchStatus())

// Wisselen van/naar voorbeeldweergave verandert het antwoord van de API
// (een beheerder ziet daar zijn eigen sloten), dus opnieuw ophalen.
watch(() => ui.previewMode, () => accessStore.fetchStatus())

async function onSiteUnlocked() {
  await accessStore.refresh()
  await sectionStore.fetchTree(ui.visitorView)
  if (route.meta.docKind === 'home') await pageStore.fetchHomepage()
}

// Marketing-, auth- en uitnodigingsschermen staan buiten de documentatie en
// mogen nooit achter het slot verdwijnen: daar moet je juist kunnen inloggen
// of registreren. Het CMS zit al achter requiresAuth.
const GATE_EXEMPT_ROUTES = new Set([
  'login', 'forgot-password', 'reset-password', 'handoff', 'signup',
  'invitation-accept', 'landing', 'landing-nl', 'terms', 'terms-nl',
  'privacy', 'privacy-nl', 'disclaimer', 'disclaimer-nl', 'checkout',
])

// Het slotscherm over de volle breedte alleen wanneer er écht niets te zien
// is. Staat er een categorie expliciet op openbaar, dan toont de app de
// gewone site met alleen dat deel; ontgrendelen kan dan via de knop in de
// header (TheHeader).
const showSiteGate = computed(() =>
  accessStore.siteLocked
    && !accessStore.hasPublicContent
    && !GATE_EXEMPT_ROUTES.has(String(route.name))
)

/* ── Figma-sync-events: de store luistert op een privékanaal per gebruiker
   (sync gestart/klaar/mislukt). Zonder deze aanroep werd er nergens
   geabonneerd en bleef een sync in de UI eeuwig "bezig" draaien, ook nadat
   de job allang klaar of mislukt was. ── */
const authStore = useAuthStore()
const figmaStore = useFigmaStore()

watch(
  () => authStore.user?.id,
  (userId) => {
    if (userId) figmaStore.initializeWebSocket(userId)
  },
  { immediate: true }
)

/* ── Platformmelding (onderhoud, storing, aankondiging) ──
   Beheerd door Pentrical zelf, gelijk in elke hub. Opnieuw ophalen bij
   in- en uitloggen: welke meldingen je mag zien hangt daarvan af (een
   melding kan op "alleen ingelogde gebruikers" staan), en de server
   beslist dat op basis van het meegestuurde token. */
const platformAlerts = usePlatformAlertStore()
onMounted(() => platformAlerts.fetchAlerts())

/* ── Draait dit tabblad nog de code die de server serveert? ──
   Na een uitrol blijft een openstaand tabblad de oude code draaien tot het
   herlaadt, en niets vertelt je dat. Zie stores/appVersion. ── */
const appVersion = useAppVersionStore()
onMounted(() => appVersion.start())
onUnmounted(() => appVersion.stop())
watch(() => authStore.user?.id, () => platformAlerts.fetchAlerts())

/* ── Toegangsstatus volgt in- en uitloggen ──
   Inloggen is een client-side navigatie (geen page-load), dus zonder dit
   bleef de status staan op wat hij vóór het inloggen was: een teamlid dat
   via het slotscherm inlogde, kwam terug op datzelfde slotscherm. */
watch(() => authStore.user?.id, () => accessStore.fetchStatus())

/* ── Actieve hoofdcategorie volgt de route/pagina ── */
const PAGE_ROUTES = new Set([
  'home', 'page', 'page-editor', 'public-page', 'public-tab',
  // Dezelfde schermen, maar in een niet-primaire hub (§14).
  'hub-home', 'hub-public-page', 'hub-public-tab',
])

function syncActiveRoot() {
  if (sectionStore.tree.length === 0) return
  // Publieke routes dragen de categorie in de URL (sectionSlug).
  const slug = route.params.sectionSlug as string | undefined
  if (slug) {
    const section = sectionStore.sections.find(s => s.slug === slug)
    if (section) {
      const root = sectionStore.rootOf(section.id)
      if (root) sectionStore.setActiveRoot(root.id)
    }
    return // onbekende slug: laatste context behouden
  }
  // Alleen op paginaroutes afleiden van de geladen pagina; op /pages,
  // /settings e.d. zou een stale currentPage de context omgooien.
  if (!PAGE_ROUTES.has(String(route.name))) return
  const sid = pageStore.currentPage?.sectionId ?? pageStore.currentPage?.section_id
  if (sid) {
    const root = sectionStore.rootOf(sid)
    if (root) sectionStore.setActiveRoot(root.id)
  }
}

watch(
  [() => route.fullPath, () => pageStore.currentPage?.id, () => sectionStore.tree],
  syncActiveRoot,
  { immediate: true }
)

/* ── Mobiele navigatie-drawer: zelfde opzet als AccessibleModal.vue
   (focus-trap, focus-restore, body-scroll-lock, sr-aankondiging). ── */
const mobileNavRef = ref<HTMLElement | null>(null)
const { saveFocus, restoreFocus, trapFocus, focusFirst } = useFocusManagement()
const { announceDialogOpen, announceDialogClose } = useAnnounce()
let cleanupMobileNavTrap: (() => void) | null = null

watch(() => ui.isMobileNavOpen, (open) => {
  document.body.style.overflow = open ? 'hidden' : ''
  if (open) {
    saveFocus()
    nextTick(() => {
      if (mobileNavRef.value) {
        cleanupMobileNavTrap = trapFocus(mobileNavRef.value)
        focusFirst(mobileNavRef.value)
      }
    })
    announceDialogOpen(t('chrome.sidebar.label'))
  } else {
    cleanupMobileNavTrap?.()
    cleanupMobileNavTrap = null
    restoreFocus()
    announceDialogClose()
  }
})

onUnmounted(() => {
  cleanupMobileNavTrap?.()
  document.body.style.overflow = ''
})

// Routes met een eigen, volledige layout (geen app-chrome): het CMS, de
// marketingpagina's (landing/voorwaarden/privacy/disclaimer — Pentrical-
// merkstijl, los van het tenant-thema) en de auth-schermen. Inloggen/
// registreren horen als losse schermen tussen website en app in — niet
// "half in de app" met documentatieheader en -menu terwijl je nog niet
// binnen bent.
const isFullscreenRoute = computed(() => {
  return ['landing', 'landing-nl', 'terms', 'terms-nl', 'privacy', 'privacy-nl', 'disclaimer', 'disclaimer-nl', 'login', 'forgot-password', 'reset-password', 'handoff', 'signup', 'invitation-accept', 'checkout', 'unknown-environment'].includes(String(route.name))
})

// Sidebar verbergen: op de loginpagina, op de homepage wanneer daar
// "linkermenu verbergen" is ingesteld, en op het settings- en account-scherm
// (die hebben hun eigen linkermenu; de Figma-detailpagina hoort daarbij).
const showSidebar = computed(() => {
  if (route.name === 'login') return false
  if (route.meta.docKind === 'home' && pageStore.homepageHideSidebar) return false
  if (route.path.startsWith('/settings')) return false
  if (route.path.startsWith('/account')) return false
  if (route.name === 'figma-file') return false
  // De interactieve viewer heeft alle breedte nodig voor canvas + specs
  if (route.name === 'figma-viewer') return false
  return true
})

// De mobiele drawer (< lg) is de énige navigatie op smalle schermen. Op de
// homepage met "linkermenu verbergen" is de desktop-sidebar bewust weg, maar
// de mobiele gebruiker heeft de drawer nog nodig — anders doet de hamburger
// daar niets. Dus: drawer altijd op de homepage, verder gelijk aan de sidebar.
const showMobileNav = computed(() => showSidebar.value || route.meta.docKind === 'home')

// De platformmelding hoort bij de hubs, niet bij de website van Pentrical
// zelf: op de landingspagina en de juridische pagina's blijft hij weg. Op
// alle overige schermen staat hij er wél — ook op het inlogscherm en in het
// CMS, want "de documentatie is zo even onbereikbaar" is juist dán relevant.
const MARKETING_ROUTES = new Set([
  'landing', 'landing-nl', 'terms', 'terms-nl',
  'privacy', 'privacy-nl', 'disclaimer', 'disclaimer-nl',
])

// Ook niet op een onbekende omgeving: die balk hoort bij een omgeving, en
// juist die bestaat daar niet. Bovendien haalt hij zijn meldingen op bij de
// server, wat op dat adres per definitie mislukt.
const showPlatformAlerts = computed(() =>
  !MARKETING_ROUTES.has(String(route.name)) && route.name !== 'unknown-environment'
)

// Viewer vult exact het venster: geen buitenscroll, padding of footer —
// het canvas krijgt anders geen begrensde hoogte en de pagina gaat scrollen.
const viewportLocked = computed(() => route.name === 'figma-viewer')

// Mobiele navigatie-drawer sluiten na het volgen van een link.
watch(() => route.fullPath, () => ui.closeMobileNav())

// Apply saved accessibility preferences on mount
onMounted(() => {
  const savedA11yPrefs = localStorage.getItem('accessibility_preferences')
  if (savedA11yPrefs) {
    const prefs = JSON.parse(savedA11yPrefs)
    const root = document.documentElement
    
    if (prefs.reduceMotion) root.classList.add('reduce-motion')
    if (prefs.highContrast) root.classList.add('high-contrast')
    if (prefs.largeText) root.classList.add('large-text')
  }
})
</script>

<template>
  <div class="app-container h-screen overflow-hidden bg-gray-50 flex flex-col" :lang="localeStore.locale">
    <!-- Skip link for keyboard users - WCAG 2.4.1 -->
    <SkipLink :label="t('a11y.skipToContent')" />
    
    <!-- Live region for screen reader announcements - WCAG 4.1.3 -->
    <LiveRegion />
    
    <!-- Toast notifications -->
    <NotificationToast />

    <!-- Voortgang van een lopende Figma-sync (globaal: blijft zichtbaar
         waar je ook bent binnen Pentrical) -->
    <SyncProgressPopover />

    <!-- Platformmelding van Pentrical: onderhoud, storing, aankondiging.
         Helemaal bovenin, boven álle app-chrome (beheerbalk en header) en
         ook boven het slotscherm — een storing hoort een bezoeker te
         bereiken vóórdat hij een wachtwoord intikt dat toch niets oplevert.
         Vandaar dat deze buiten de drie layouts hieronder staat. -->
    <PlatformAlertBar v-if="showPlatformAlerts" class="flex-none" />

    <!-- Er staat een nieuwe versie klaar. Hier, naast de platformmelding en
         buiten de layouts: een tabblad dat oude code draait kan overal open
         staan, ook op een publieke documentatiepagina. -->
    <NewVersionBar class="flex-none" />

    <!-- Alles wat niet de meldingsbalk is, vult de resterende hoogte. De
         layouts hieronder rekenen daarom met h-full en niet meer met
         h-screen: met een melding erboven is het venster niet meer de
         beschikbare ruimte. -->
    <div class="flex-1 min-h-0">

    <!-- Slotscherm: de site is afgeschermd en deze bezoeker is er nog niet
         doorheen. Staat vóór alle layouts, maar laat de auth- en marketing-
         routes bewust ongemoeid (zie GATE_EXEMPT_ROUTES) — daar moet je juist
         kunnen inloggen. De URL blijft staan, dus na ontgrendelen land je op
         de pagina waar de link naartoe wees. -->
    <template v-if="showSiteGate">
      <div class="h-full overflow-y-auto">
        <AccessGate
          variant="page"
          :mode="accessStore.mode === 'team' ? 'team' : 'password'"
          :hint="accessStore.hint"
          @unlocked="onSiteUnlocked"
        />
      </div>
    </template>

    <!-- Schermen zonder app-omlijsting (inloggen, aanmelden, juridisch). -->
    <!-- overflow-hidden, net als de app-root vroeger: deze schermen
         regelen hun eigen scrollgebied (zie AuthShell). -->
    <template v-else-if="isFullscreenRoute">
      <div class="h-full overflow-hidden">
        <RouterView />
      </div>
    </template>

    <!-- Standard application layout: app-shell -->
    <!-- Header over de volle breedte; daaronder sidebar + content, elk
         met eigen scrollgebied. Inklappen van de sidebar raakt de header niet. -->
    <template v-else>
      <div class="h-full flex flex-col">
        <!-- Beheerbalk: alleen ingelogd en niet in voorbeeldweergave.
             canEdit en previewMode sluiten elkaar uit, dus er is altijd
             hooguit één balk boven de header. -->
        <TheAdminBar v-if="ui.canEdit" class="flex-none" />

        <!-- Voorbeeldweergave-balk: ingelogd, maar kijkend als bezoeker -->
        <div
          v-if="ui.previewMode"
          class="flex-none flex items-center justify-center gap-3 bg-amber-100 px-4 py-2 text-sm text-amber-800 border-b border-amber-200"
          role="status"
        >
          <Eye class="w-4 h-4" aria-hidden="true" />
          <span>{{ t('chrome.preview.banner') }}</span>
          <button
            type="button"
            class="font-semibold underline underline-offset-2 hover:text-amber-950 focus:outline-none focus:ring-2 focus:ring-amber-600 rounded"
            @click="stopPreview()"
          >
            {{ t('chrome.preview.back') }}
          </button>
        </div>

        <!-- Header (landmark: banner) — volle breedte, incl. logo en login.
             In de viewer verborgen om verticale ruimte te sparen; de
             beheerbalk (indien ingelogd) blijft als enige chrome staan. -->
        <TheHeader v-if="!viewportLocked" class="flex-none" />

        <div class="flex flex-1 min-h-0">
          <!-- Sidebar navigation (landmark: navigation) — onder de header -->
          <TheSidebar
            v-if="showSidebar"
            class="hidden lg:flex flex-none"
          />

          <!-- Mobiele navigatie: dezelfde documentatieboom als drawer (< lg).
               Backdrop sluit bij klikken, Escape sluit, focus is
               gevangen zolang de drawer open is (zie watch hierboven). -->
          <template v-if="showMobileNav">
            <Transition name="fade">
              <div
                v-if="ui.isMobileNavOpen"
                class="fixed inset-0 z-40 bg-black/40 lg:hidden"
                aria-hidden="true"
                @click="ui.closeMobileNav()"
              ></div>
            </Transition>
            <!-- De lade komt van rechts, aan de kant van de knop die hem
                 opent (zie TheHeader). -->
            <Transition name="slide-right">
              <div
                v-if="ui.isMobileNavOpen"
                id="mobile-menu"
                ref="mobileNavRef"
                class="fixed inset-y-0 right-0 z-50 w-72 max-w-[85vw] flex flex-col shadow-xl lg:hidden"
                style="background-color: #E5EBED;"
                role="dialog"
                aria-modal="true"
                :aria-label="t('chrome.sidebar.label')"
                @keydown.esc="ui.closeMobileNav()"
              >
                <div class="flex items-center justify-end px-4 py-3 border-b border-gray-300">
                  <button
                    type="button"
                    class="p-2 rounded-lg text-gray-500 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    @click="ui.closeMobileNav()"
                  >
                    <span class="sr-only">{{ t('chrome.sidebar.close') }}</span>
                    <X class="w-5 h-5" aria-hidden="true" />
                  </button>
                </div>
                <nav class="flex-1 overflow-y-auto px-3 py-3" :aria-label="t('chrome.sidebar.docs')">
                  <SidebarNav />
                </nav>
              </div>
            </Transition>
          </template>

          <!-- Main content area (eigen scrollgebied; viewer: geen scroll) -->
          <div
            class="flex-1 min-w-0 overscroll-contain flex flex-col"
            :class="viewportLocked ? 'overflow-hidden' : 'overflow-y-auto'"
          >
            <!-- Main content (landmark: main) -->
            <main
              id="main-content"
              class="flex-1"
              :class="viewportLocked ? 'min-h-0' : 'page-pad'"
              tabindex="-1"
              :aria-label="t('chrome.mainLabel')"
            >
              <RouterView />
            </main>

            <!-- Footer (landmark: contentinfo) -->
            <AppFooter v-if="!viewportLocked" />
          </div>
        </div>
      </div>
    </template>

    </div><!-- /resterende hoogte onder de meldingsbalk -->
  </div>
</template>

<style>
/* Global app styles */
.app-container {
  /* Ensure proper text rendering */
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* Focus styles for all interactive elements */
:focus-visible {
  outline: 2px solid theme('colors.primary.600');
  outline-offset: 2px;
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}

/* Mobiele navigatie-drawer */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-right-enter-active,
.slide-right-leave-active {
  transition: transform 0.2s ease;
}
.slide-right-enter-from,
.slide-right-leave-to {
  transform: translateX(100%);
}
</style>
